import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../shared/apiClient';
import { resolveBillingPlansFromApi } from '../../shared/billing/defaultPublicPlans';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { PlanCard, type BillingCycle, type PlanCardPlan } from '../PlanCard/PlanCard';
import { brand } from '../../shared/config/brand';
import './Paywall.css';

export const Paywall: React.FC<{ onDismiss?: () => void }> = ({ onDismiss }) => {
  const navigate = useNavigate();
  const { currentOrg } = useAuth();
  const { refresh } = useSubscription();
  const [plans, setPlans] = useState<PlanCardPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/billing/plans');
        if (!res.ok) throw new Error('Failed to load plans');
        const data = await res.json();
        if (!cancelled) setPlans(resolveBillingPlansFromApi(data.plans));
      } catch {
        if (!cancelled) setPlans(resolveBillingPlansFromApi(null));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const startCheckout = useCallback(
    async (planSlug: string) => {
      if (!currentOrg?.id || planSlug === 'enterprise') return;
      setCheckoutLoading(planSlug);
      try {
        const res = await apiFetch('/api/billing/checkout', {
          method: 'POST',
          headers: {
            'X-Organization-Id': currentOrg.id,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            planSlug,
            billingCycle,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if ((data as { error?: string }).error === 'enterprise_contact_sales') {
            navigate('/pricing');
            return;
          }
          setCheckoutLoading(null);
          return;
        }
        const url = (data as { url?: string }).url;
        if (url) {
          window.location.href = url;
        }
        await refresh();
      } catch {
        setCheckoutLoading(null);
      }
    },
    [currentOrg?.id, billingCycle, navigate, refresh]
  );

  return (
    <div className="paywall-page">
      <div className="paywall-backdrop" aria-hidden />
      <div className="paywall-modal animate-fade-in">
        {onDismiss && (
          <button type="button" className="paywall-close" onClick={onDismiss} aria-label="Close">
            &times;
          </button>
        )}
        <h1 className="paywall-title">Your trial has ended</h1>
        <p className="paywall-sub">
          Choose a plan to continue using Relix. You can also review pricing and guarantees on our
          public pricing page.
        </p>
        <div className="paywall-billing-toggle">
          <button
            type="button"
            className={billingCycle === 'monthly' ? 'active' : ''}
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly
          </button>
          <button
            type="button"
            className={billingCycle === 'annual' ? 'active' : ''}
            onClick={() => setBillingCycle('annual')}
          >
            Annual <span className="paywall-save">Save 17%</span>
          </button>
        </div>
        {loading ? (
          <div className="paywall-loading">
            <Loader2 className="paywall-spin" />
          </div>
        ) : (
          <div className="paywall-grid">
            {plans.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                billingCycle={billingCycle}
                featured={p.slug === 'scale'}
                badge={p.slug === 'scale' ? 'Most popular' : undefined}
                isEnterprise={p.slug === 'enterprise'}
                ctaLabel={
                  p.slug === 'enterprise'
                    ? 'Contact sales'
                    : checkoutLoading === p.slug
                      ? 'Redirecting…'
                      : 'Subscribe'
                }
                disabled={!!checkoutLoading && checkoutLoading !== p.slug}
                onCta={() => {
                  if (p.slug === 'enterprise') {
                    window.location.href = `mailto:${brand.salesEmail}?subject=Enterprise%20plan`;
                    return;
                  }
                  void startCheckout(p.slug);
                }}
              />
            ))}
          </div>
        )}
        <button type="button" className="paywall-link" onClick={() => navigate('/pricing')}>
          View full pricing & FAQ
        </button>
      </div>
    </div>
  );
};
